import React, { Fragment, Component } from 'react';
import { Button } from 'semantic-ui-react';
import { DiagramEngine, DiagramModel } from 'storm-react-diagrams';
import 'storm-react-diagrams/dist/style.min.css';
import styled from 'styled-components';

import { Row } from '@cuemath/comps';
import {
  BLOCK_TYPE_OPTIONS,
  SECTION_CODE_OPTIONS,
  TAB_CODE_OPTIONS,
} from 'constants/block-options';
import { sentryWarningMessage } from 'modules/sentry';
import { ActivityNodeModel } from './activity-node';
import ActivitySheets from './activity-chapters';
import { assignBlockToSheet, incomingNewSheetSeparator } from './activity-chapters/utils';

const GapCreator = styled.div`
  margin-top: 10px;
`;

const defaultCriteria = {
  attribute: '',
  operator: '',
  attributeValue: '',
  selected: false,
};

class ActivityGraph extends Component {
  constructor(props) {
    super(props);
    this.state = {
      engine: new DiagramEngine(),
      model: new DiagramModel(),
      selectedNodeId: '',
      blockDataList: [],
      dependencyDataList: [],
      linkedDataList: {},
      testBlockWarning: true,
      puzzleBlockWarning: true,
      warningPortalOpen: false,
      warningPortalMessage: '',
      memoBlockData: null,
    };

    this.addNode = this.addNode.bind(this);

    this.setBlockDataList = this.setBlockDataList.bind(this);
  }

  componentDidMount() {
    const { engine, model } = this.state;
    const { nodeDataList, linkDataList } = this.props;

    Object.values(nodeDataList).forEach((nodeData, i) => {
      this.addNode(nodeData.id, nodeData, true);
    });

    // load model into engine
    engine.setDiagramModel(model);
    this.setState({
      engine: engine,
      model: model,
      linkedDataList: linkDataList,
    });
  }

  addNode(nodeID, nodeData, toggleUpdate) {
    const { isChapter } = this.props;
    const { model } = this.state;
    if (toggleUpdate) {
      nodeData['selected'] = true;
    }
    var node = new ActivityNodeModel(nodeData, nodeID, isChapter);
    model.addNode(node);
    // TODO: if(model.getNodes()) etc.
    if (!toggleUpdate) {
      this.forceUpdate();
    }
  }

  componentDidUpdate(prevProps) {
    const { chapterCode: prevChapterCode } = prevProps;
    const { chapterCode: updatedChapterCode } = this.props;
    if (prevChapterCode !== updatedChapterCode) {
      this.deleteBlocksFromNodes();
    }
  }

  deleteBlocksFromNodes = () => {
    const { model } = this.state;
    Object.values(model.getNodes()).forEach(node => {
      const { activityNodeData } = node;
      const { block, ...updatedActivityNodeData } = activityNodeData || {};
      node.setActivityNodeData(updatedActivityNodeData);
    });
    this.forceUpdate();
  };

  setBlockData = blockData => {
    const {
      model,
      engine,
      selectedNodeId,
      testBlockWarning,
      puzzleBlockWarning,
    } = this.state;
    const { selectionBoxNodes } = this.props;
    if (blockData) {
      selectionBoxNodes.forEach(
        nodeId => {
          model.getNode(nodeId).setBlockData(blockData);
        },
        () => this.forceUpdate(),
      );

      const activityNodeData =
        model.getNode(selectedNodeId) && model.getNode(selectedNodeId).activityNodeData;

      if (Array.isArray(selectedNodeId) && selectedNodeId.length > 0) {
        let errorInSelectedNode = null;
        selectedNodeId.forEach(nodeId => {
          const tempActivityNodeData = model.getNode(nodeId).activityNodeData;

          if (
            blockData.tab_code === TAB_CODE_OPTIONS.MASTERY &&
            blockData.section_code === SECTION_CODE_OPTIONS.TESTS &&
            blockData.block_type !== BLOCK_TYPE_OPTIONS.TESTS &&
            testBlockWarning
          ) {
            const warningDetails = {
              activityNodeData: tempActivityNodeData,
              condition: `Block code is not TESTS`,
              message: `The Section code is 'TESTS' but the block code is not 'TEST'.`,
              errorLevel: 'warning',
            };

            sentryWarningMessage(warningDetails);
            errorInSelectedNode = 'from Test';
            return;
          }

          if (
            blockData.tab_code === TAB_CODE_OPTIONS.PUZZLE_CARDS &&
            blockData.section_code === SECTION_CODE_OPTIONS.PUZZLE_CARDS &&
            blockData.block_type !== BLOCK_TYPE_OPTIONS.PUZZLE_CARDS &&
            puzzleBlockWarning
          ) {
            const warningDetails = {
              activityNodeData: tempActivityNodeData,
              condition: 'Block code is not PUZZLE_CARDS',
              message: `The Tab and Section codes are 'PUZZLE CARDS' but the block code is not 'PUZZLE CARDS'.`,
              errorLevel: 'warning',
            };

            sentryWarningMessage(warningDetails);
            errorInSelectedNode = 'from Puzzle';
            return;
          }
        });

        if (errorInSelectedNode) {
          this.setState({
            warningPortalOpen: true,
            warningPortalMessage:
              errorInSelectedNode === 'from Test'
                ? `The Section code is 'TESTS' but the block code is not 'TEST'. Do you want to continue?`
                : `The Tab and Section codes are 'PUZZLE CARDS' but the block code is not 'PUZZLE CARDS'.`,
            memoBlockData: blockData,
            testBlockWarning: errorInSelectedNode === 'from Puzzle' ? false : true,
          });
          return;
        }
      } else {
        if (activityNodeData) {
          if (
            blockData.tab_code === TAB_CODE_OPTIONS.MASTERY &&
            blockData.section_code === SECTION_CODE_OPTIONS.TESTS &&
            blockData.block_type !== BLOCK_TYPE_OPTIONS.TESTS &&
            testBlockWarning
          ) {
            const warningDetails = {
              activityNodeData: activityNodeData,
              condition: `Block code is not TESTS`,
              message: `The Section code is 'TESTS' but the block code is not 'TEST'.`,
              errorLevel: 'warning',
            };

            sentryWarningMessage(warningDetails);

            this.setState({
              warningPortalOpen: true,
              warningPortalMessage: `The Section code is 'TESTS' but the block code is not 'TEST'. Do you want to continue? `,
              memoBlockData: blockData,
            });
            return;
          }

          if (
            blockData.tab_code === TAB_CODE_OPTIONS.PUZZLE_CARDS &&
            blockData.section_code === SECTION_CODE_OPTIONS.PUZZLE_CARDS &&
            blockData.block_type !== BLOCK_TYPE_OPTIONS.PUZZLE_CARDS &&
            puzzleBlockWarning
          ) {
            const warningDetails = {
              activityNodeData: activityNodeData,
              condition: 'Block code is not PUZZLE_CARDS',
              message: `The Tab and Section codes are 'PUZZLE CARDS' but the block code is not 'PUZZLE CARDS'.`,
              errorLevel: 'warning',
            };

            sentryWarningMessage(warningDetails);

            this.setState({
              warningPortalOpen: true,
              warningPortalMessage: `The Tab and Section codes are 'PUZZLE CARDS' but the block code is not 'PUZZLE CARDS'. Do you want to continue?`,
              memoBlockData: blockData,
            });

            return;
          }
        }
      }

      assignBlockToSheet(blockData, engine, selectedNodeId);
    }
  };

  setBlockDataList(blockDataList) {
    this.setState({
      blockDataList,
    });
  }

  render() {
    const { chapterCode, programId, nodeDataList, updateBlockData } = this.props;

    const { engine, blockDataList, dependencyDataList, linkedDataList } = this.state;

    const { nodeDataList: dataList, extraSheetList } =
      incomingNewSheetSeparator(engine, blockDataList) || {};

    const { masteryCardData } = dataList || {};

    return (
      <Fragment>
        <GapCreator>
          <Row>
            <ActivitySheets
              nodeDataList={nodeDataList}
              masteryCardData={masteryCardData}
              extraSheetList={extraSheetList}
              chapterCode={chapterCode}
              programId={programId}
              updateBlockData={updateBlockData}
              setBlockDataList={this.setBlockDataList}
              setBlockData={this.setBlockData}
              blockDataList={blockDataList || []}
              dependencyDataList={dependencyDataList || []}
              linkedDataList={linkedDataList || {}}
            />
          </Row>
        </GapCreator>
      </Fragment>
    );
  }
}

export default ActivityGraph;
